import React from 'react';
import {useVirtualizer} from '@packages/systems/spring-hooks';

import {CREATABLE_OPTION_VALUE} from '../../../hooks';
import {RowItem} from '../../RowItem';
import {RowItemCheckbox} from '../../RowItemCheckbox';

import {
  useSearchableListIndexContext,
  useSearchableListSelectionContext,
} from '../useSearchableList';

import type {OptionType} from '../_types';
import type {SearchableListBoxProps} from '../SearchableListBox';

type SearchableListItemContextType = ReturnType<
  typeof useSearchableListItem
> & {
  density: SearchableListBoxProps['density'];
  /**
   * Used to dynamically measure items in virtualized lists.
   */
  measureElement: ReturnType<typeof useVirtualizer>['measureElement'];
  verticalItemAlignment: SearchableListBoxProps['verticalItemAlignment'];
};

export const SearchableListItemContext =
  React.createContext<SearchableListItemContextType | null>(null);

export const useSearchableListItemContext =
  (): SearchableListItemContextType => {
    const context = React.useContext(SearchableListItemContext);
    const isTestEnvironment = process.env.NODE_ENV === 'test';

    if (!context && !isTestEnvironment) {
      throw new Error('You must use component within SearchableListContext');
    }

    /**
     * Context should only be null in a test environment.
     * We're okay with this because we want to be able to test SearchableListItem's subcomponents in isolation.
     */
    // @ts-expect-error TS2322 "null is not assignable to type SearchableListItemContextType"
    return context;
  };

export const useSearchableListItem = (
  value: OptionType['value'],
  /**
   * Represents the position of an item within the flattened Options array.
   * It is used to coordinate virtualization by bridging Floating-UI's list navigation and Tanstack's virtualizer.
   */
  virtualIndex?: number
) => {
  const id = React.useId();

  const {
    checkmarkStyle,
    getIsSelected,
    hasMaxSelections,
    inputRef,
    isDisabled,
    onCreateOption,
    selectionMode,
    toggleSelectedValue,
  } = useSearchableListSelectionContext();

  const {activeIndex, getItemProps, listRef, setActiveIndex, getIndexByValue} =
    useSearchableListIndexContext();

  const listIndex = React.useMemo(
    () => getIndexByValue?.(value) ?? 0,
    [getIndexByValue, value]
  );

  const isActive = React.useMemo(
    () => listIndex === activeIndex,
    [activeIndex, listIndex]
  );

  /**
   *
   * We need to memoize `itemProps` because due to significant performance issues
   * The return from `getItemProps` is not memoized internally, which means that it returns a new object every time it is called.
   * This triggers child elements to re-render constantly because their item prop callbacks are referentially unstable.
   */
  const itemProps = React.useMemo(
    () =>
      getItemProps({
        ref: (node) => {
          /**
           * Register the SearchableListItem in the listRef.
           * We are using optionIndex, because that is its order in the options array as `useListNavigation` sees them (i.e. flattened).
           */
          if (typeof listIndex === 'number' && node !== null) {
            // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
            listRef.current[listIndex] = node;
          }
        },
        onClick() {
          if (value === CREATABLE_OPTION_VALUE) {
            /**
             * We are using the inputRef to get the value of the SearchableListInput.
             * We are using the raw DOM info, because keeping this closure updated with inputValue would re-render every row whenever a user types.
             *
             * By using the DOM as our source of truth, we can get the latest value without requiring closure updates here.
             */
            onCreateOption?.(inputRef?.current?.value?.trim() ?? '');
          } else {
            toggleSelectedValue(value);
          }

          /**
           * Set focus on SearchableListInput
           */
          inputRef?.current?.focus();
          setActiveIndex?.(listIndex);
        },
        onMouseDown(e) {
          /**
           * Prevent SearchableListItem from receiving DOM focus
           * DOM focus should remain on SearchableListItem
           */
          e.preventDefault();
        },
      }),
    [
      getItemProps,
      listIndex,
      listRef,
      onCreateOption,
      toggleSelectedValue,
      value,
      inputRef,
      setActiveIndex,
    ]
  );

  const isSelected = getIsSelected(value);

  const Component = selectionMode === 'multiple' ? RowItemCheckbox : RowItem;

  return React.useMemo(() => {
    return {
      checkmarkStyle,
      Component,
      hasMaxSelections,
      id,
      isDisabled,
      isActive,
      virtualIndex,
      listIndex,
      isSelected,
      itemProps,
      value,
    };
  }, [
    checkmarkStyle,
    Component,
    hasMaxSelections,
    id,
    isDisabled,
    isActive,
    virtualIndex,
    listIndex,
    isSelected,
    itemProps,
    value,
  ]);
};
